import React from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CloudIcon,
  CogIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const Features: React.FC = () => {
  const features = [
    {
      icon: CpuChipIcon,
      title: 'AI-Powered OCR',
      description: 'Advanced machine learning algorithms extract data from invoices with 99.5% accuracy.',
      details: [
        'Support for PDF, JPEG, PNG, WebP formats',
        'Multi-language invoice processing',
        'Handwritten text recognition',
        'Complex table structure parsing'
      ]
    },
    {
      icon: DocumentTextIcon,
      title: 'Smart Invoice Processing',
      description: 'Automatically categorize and process invoices with intelligent data validation.',
      details: [
        'Automatic vendor detection',
        'Invoice duplicate detection',
        'Tax calculation validation',
        'Custom field extraction'
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      description: 'Bank-grade security with SOC 2 compliance and end-to-end encryption.',
      details: [
        'SOC 2 Type II certified',
        'ISO 27001 compliance',
        'End-to-end encryption',
        'Regular security audits'
      ]
    },
    {
      icon: ChartBarIcon,
      title: 'Advanced Analytics',
      description: 'Real-time insights and reporting to optimize your accounting workflows.',
      details: [
        'Custom dashboard creation',
        'Automated report generation',
        'Performance metrics tracking',
        'Predictive analytics'
      ]
    },
    {
      icon: CloudIcon,
      title: 'Cloud Integration',
      description: 'Seamless integration with popular accounting software and cloud storage.',
      details: [
        'QuickBooks integration',
        'Tally ERP integration',
        'Google Drive sync',
        'Dropbox connectivity'
      ]
    },
    {
      icon: CogIcon,
      title: 'Workflow Automation',
      description: 'Automate repetitive tasks and create custom approval workflows.',
      details: [
        'Custom approval chains',
        'Automated notifications',
        'Scheduled processing',
        'Rule-based routing'
      ]
    },
    {
      icon: UserGroupIcon,
      title: 'Client Portal',
      description: 'Dedicated portal for clients to upload invoices and track processing status.',
      details: [
        'Branded client interface',
        'Secure document upload',
        'Real-time status tracking',
        'Mobile-friendly design'
      ]
    },
    {
      icon: ClockIcon,
      title: 'Real-time Processing',
      description: 'Process invoices instantly with real-time status updates and notifications.',
      details: [
        'Instant processing alerts',
        'Live status dashboard',
        'Email notifications',
        'SMS updates available'
      ]
    }
  ];

  const benefits = [
    {
      icon: ArrowTrendingUpIcon,
      title: '80% Time Savings',
      description: 'Reduce manual data entry and processing time significantly'
    },
    {
      icon: CheckCircleIcon,
      title: '99.5% Accuracy',
      description: 'Minimize errors with AI-powered data extraction'
    },
    {
      icon: BoltIcon,
      title: '10x Faster Processing',
      description: 'Process invoices in seconds, not hours'
    },
    {
      icon: GlobeAltIcon,
      title: '24/7 Availability',
      description: 'Cloud-based solution accessible anytime, anywhere'
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Powerful Features for
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                {' '}Modern CA Firms
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how AICaFriend's advanced AI technology and comprehensive feature set 
              can transform your invoice processing workflow and boost productivity.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose AICaFriend?</h2>
            <p className="text-lg text-gray-600">Measurable results that transform your practice</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-4">
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Features */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete Feature Set</h2>
            <p className="text-lg text-gray-600">Everything you need to streamline your invoice processing</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 mb-4">{feature.description}</p>
                    <ul className="space-y-2">
                      {feature.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-sm text-gray-500">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Integration Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Seamless Integrations</h2>
            <p className="text-lg text-gray-600">Connect with your existing tools and workflows</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {[
              'QuickBooks', 'Tally ERP', 'Google Drive', 'Dropbox', 'Excel', 'Zoho Books'
            ].map((integration, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors duration-200"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">{integration.charAt(0)}</span>
                </div>
                <p className="text-sm font-medium text-gray-900">{integration}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Transform Your Invoice Processing?
            </h2>
            <p className="text-xl text-indigo-100 mb-8">
              Join 500+ CA firms already using AICaFriend to streamline their operations.
            </p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <button className="w-full sm:w-auto bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200">
                Start Free Trial
              </button>
              <button className="w-full sm:w-auto border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors duration-200">
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Features;